import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import classNames from 'classnames';
import { Contact } from 'models/Contact';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { Button } from 'ncoded-component-library';
import { Field, Form } from 'react-final-form';
import { BasicUserInfo } from 'models/User';
import { FormApi } from 'final-form';
import { SUCCESS_MESSAGES } from 'request-responses';
import { useTranslation } from 'react-i18next';
import WarningExclamationIcon from 'icons/WarningExclamation.icon';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import InputField from 'components/InputField';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import PhoneFieldWrapper from 'components/PhoneFieldWrapper';
import api from 'api';
import TrashIcon from 'icons/Trash.icon';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import useUserBasicInfo from 'hooks/useUserBasicInfo';
import EmailField from 'components/EmailField';

import './ManageContactDrawer.styles.scss';

const FIELDS_TO_UPDATE: (keyof BasicUserInfo)[] = [
  'firstName',
  'lastName',
  'phone',
  'title',
  'companyName',
];

type ManageContactDrawerProps = {
  className?: string;
  contact?: Partial<Contact>;
  onSuccess?: (contact: Contact) => void;
  onDelete?: (id: string) => Promise<void>;
};

const ManageContactDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageContactDrawerProps
> = (props, ref) => {
  const { className, contact, onSuccess, onDelete } = props;

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const classes = classNames('manage-contact-drawer', className);

  const formRef = useRef<FormApi<Contact, Partial<Contact>> | null>(null);

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const commonValidations = useValidations();

  const {
    isFetchingUserBasicInfo,
    isUserBasicInfoSet,
    onEmailChange,
    resetState,
  } = useUserBasicInfo();

  const isAddingNewContact = !contact;
  const isEditingContact = !!contact;

  const disabledEmail = isEditingContact;

  const hasResolveAccount = isUserBasicInfoSet || contact?.existUser;

  const handleOnClose = useCallback(() => {
    drawer?.setOpen(false);
    resetState();
    formRef.current?.restart();
    formRef.current?.reset();
  }, [drawer, resetState]);

  const { t } = useTranslation();

  const initialValues = useMemo(
    () =>
      !contact
        ? null
        : {
            firstName: contact.firstName,
            lastName: contact.lastName,
            email: contact.email,
            companyName: contact.companyName,
            title: contact.title,
            phone: contact.phone || undefined,
            preApproved: contact.preApproved || false,
          },
    [contact],
  );

  const onSubmit = useCallback(
    async (values: Contact) => {
      try {
        const { firstName, lastName, phone, preApproved, companyName, title } =
          values;

        let contactData;

        if (isAddingNewContact) {
          const { data } = await api.contacts.createContact(values);
          contactData = data;
        } else {
          const { data } = await api.contacts.updateContact(contact._id, {
            firstName,
            lastName,
            preApproved,
            companyName: companyName || null,
            title: title || null,
            ...(phone && {
              phone: { ...phone, phoneNumber: phone.phoneNumber || null },
            }),
          });

          contactData = data;
        }

        popSuccess(
          t(
            isAddingNewContact
              ? SUCCESS_MESSAGES.CREATE_CONTACT
              : SUCCESS_MESSAGES.UPDATE_CONTACT,
          ),
        );

        onSuccess?.(contactData);
        handleOnClose();
      } catch (e) {
        popServerError(e);
      }
    },
    [
      contact,
      isAddingNewContact,
      popSuccess,
      t,
      onSuccess,
      handleOnClose,
      popServerError,
    ],
  );

  return (
    <SideDrawer ref={drawerRef} className={classes} closeAction={handleOnClose}>
      {drawer?.open && (
        <Form
          initialValues={initialValues}
          onSubmit={onSubmit}
          render={({
            submitting,
            invalid,
            pristine,
            form,
            values,
            handleSubmit,
          }) => {
            formRef.current = form;

            const phoneKey =
              values.phone?.countryCode + values.phone?.phoneNumber;

            return (
              <form onSubmit={handleSubmit}>
                <p>{t(isAddingNewContact ? 'addAContact' : 'editAContact')}</p>

                {hasResolveAccount && (
                  <p className="warning">
                    <WarningExclamationIcon />
                    {t(
                      isAddingNewContact
                        ? 'thisUserAlreadyHasAccount'
                        : 'thisContactHasEditedDetails',
                    )}
                  </p>
                )}

                <div>
                  <Field
                    name="firstName"
                    label={t('firstName')}
                    component={InputField}
                    required
                    disabled={hasResolveAccount}
                    validate={commonValidations.name}
                  />
                  <Field
                    name="lastName"
                    label={t('lastName')}
                    component={InputField}
                    required
                    disabled={hasResolveAccount}
                    validate={commonValidations.name}
                  />
                </div>

                <EmailField
                  required
                  disabled={disabledEmail}
                  onChangeHandle={(email) =>
                    onEmailChange({
                      email,
                      form,
                      fieldsToPrefill: FIELDS_TO_UPDATE,
                    })
                  }
                />

                <PhoneFieldWrapper
                  disabled={hasResolveAccount}
                  {...(hasResolveAccount && {
                    key: phoneKey,
                  })}
                />

                <Field
                  name="companyName"
                  disabled={hasResolveAccount}
                  label={t('company')}
                  component={InputField}
                />

                <Field
                  name="title"
                  disabled={hasResolveAccount}
                  label={t('professionalTitle')}
                  component={InputField}
                />

                <Field
                  name="preApproved"
                  label={t('preApprovedDescription')}
                  type="checkbox"
                  component={CheckboxField}
                />

                <div>
                  <Button
                    type="submit"
                    disabled={
                      submitting ||
                      invalid ||
                      pristine ||
                      isFetchingUserBasicInfo
                    }
                  >
                    {t(isAddingNewContact ? 'addContact' : 'updateContact')}
                  </Button>

                  {onDelete && (
                    <Button
                      type="button"
                      variant="link"
                      className="warning-action-button"
                      icon={TrashIcon}
                      onClick={async () => {
                        await onDelete(contact?._id);
                        handleOnClose();
                      }}
                    >
                      {t('delete')}
                    </Button>
                  )}
                </div>
              </form>
            );
          }}
        />
      )}
    </SideDrawer>
  );
};

export default forwardRef(ManageContactDrawer);
